/**
 * MFA enrollment challenge detection.
 *
 * MFAEnforcementMiddleware (wix-app/src/mfa_enforcement.py) answers any
 * non-exempt request from a flagged-but-unenrolled account with:
 *
 *   403 {"error": "mfa_enrollment_required",
 *        "message": "...",
 *        "mfa_setup_url": "/setup-mfa",
 *        "requires_mfa": true}
 *
 * WHY STATUS ALONE IS NOT ENOUGH
 * ------------------------------
 * 403 is already used for a completely different thing. Several analytics
 * endpoints report platform-wide figures (MRR, churn, the acquisition funnel)
 * and the backend restricts them to administrators, so `dashboardApi`
 * deliberately resolves `null` on 403 to keep a merchant's dashboard working
 * rather than erroring out a panel they were never meant to see.
 *
 * If that same branch swallowed an MFA challenge, a locked-out user would see
 * empty panels and never be sent to enrollment - the challenge would be read
 * as "you are not an admin". So the two cases MUST be told apart by the body,
 * not the status code, and the MFA check has to run FIRST.
 *
 * WHY THE BODY IS CLONED
 * ----------------------
 * A Response body is a single-use stream. Reading it here would leave the
 * caller with nothing to read, so every inspection happens on `res.clone()`.
 */

/** Where the backend wants the user sent. Matches the /setup-mfa route. */
export const MFA_SETUP_PATH = '/setup-mfa';

/** Dispatched on `window` so non-React fetchers can reach React. */
export const MFA_CHALLENGE_EVENT = 'contentsage:mfa-enrollment-required';

/**
 * Dispatched when the challenge no longer applies - the user enrolled, or a
 * request that would have been refused came back ok.
 *
 * A challenge MUST be revocable. It is remembered at module scope so it
 * survives navigation, which means that without this event a user who
 * enrolled successfully would keep being redirected to enrollment by the
 * route guard, and the enrollment page would then refuse to start a second
 * enrollment ("MFA is already enabled") - a dead end reachable only by a full
 * page reload. That is the same lockout this whole feature exists to prevent.
 */
export const MFA_CHALLENGE_CLEARED_EVENT = 'contentsage:mfa-challenge-cleared';

export interface MfaChallenge {
  /** Path to send the user to in order to enrol. */
  setupUrl: string;
  /** Human-readable reason, straight from the backend. */
  message: string;
}

export interface MfaChallengeEventDetail {
  challenge: MfaChallenge;
}

/**
 * Thrown instead of a generic `Error` so a caller can distinguish "you must
 * enrol" from "the request failed" and avoid rendering it as a broken panel.
 */
export class MfaEnrollmentRequiredError extends Error {
  readonly challenge: MfaChallenge;

  constructor(challenge: MfaChallenge) {
    super(challenge.message);
    this.name = 'MfaEnrollmentRequiredError';
    this.challenge = challenge;
  }
}

/**
 * Return the challenge if this response is an MFA enrollment challenge,
 * otherwise null. Safe to call on any response, ok or not.
 */
export async function detectMfaChallenge(
  res: Response,
): Promise<MfaChallenge | null> {
  if (res.status !== 403) return null;

  let body: unknown;
  try {
    // Clone: the caller still needs the original body.
    body = await res.clone().json();
  } catch {
    // A 403 with no JSON body is not an MFA challenge. An admin-only refusal
    // or an opaque proxy 403 lands here, and must fall through unchanged.
    return null;
  }

  if (!body || typeof body !== 'object') return null;
  const payload = body as Record<string, unknown>;

  // `error` is the contract; `requires_mfa` is accepted as a belt-and-braces
  // second signal in case the error slug is ever renamed.
  const isChallenge =
    payload.error === 'mfa_enrollment_required' || payload.requires_mfa === true;
  if (!isChallenge) return null;

  const setupUrl =
    typeof payload.mfa_setup_url === 'string' && payload.mfa_setup_url
      ? payload.mfa_setup_url
      : MFA_SETUP_PATH;

  const message =
    typeof payload.message === 'string' && payload.message
      ? payload.message
      : 'Multi-factor authentication is required for your account.';

  return { setupUrl, message };
}

/**
 * Tell the app the MFA challenge no longer applies.
 *
 * Call this when enrollment completes. Dispatching an event rather than
 * importing the store keeps this module free of a dependency on the React
 * hook that consumes it.
 */
export function announceMfaResolved(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MFA_CHALLENGE_CLEARED_EVENT));
}

/** Tell the app an MFA challenge happened, so the router can redirect. */
export function announceMfaChallenge(challenge: MfaChallenge): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<MfaChallengeEventDetail>(MFA_CHALLENGE_EVENT, {
      detail: { challenge },
    }),
  );
}

/**
 * Endpoints that have actually been refused with an MFA challenge.
 *
 * WHY A LEDGER AND NOT "ANY 200 MEANS WE ARE DONE"
 * ------------------------------------------------
 * Both revocation points used to treat any ok response as proof the challenge
 * was over. That is false while the account is still unenrolled, because the
 * enforcement middleware keeps an exempt list - /api/v1/mfa above all, so the
 * user can always reach the page that unblocks them. The enrolment screen's
 * own POST /api/v1/mfa/enroll therefore answered 200 and revoked the very
 * challenge that sent the user there, which is why `useMfaChallenge()` read
 * null on /setup-mfa and the "I'll do this later" escape hatch reappeared
 * during forced enforcement.
 *
 * A 200 only proves the challenge is over if THIS endpoint was refused
 * before. That keeps the original safety net - a stale challenge still clears
 * as soon as a genuinely gated request starts answering - without letting an
 * exempt route speak for the gated ones.
 */
const refusedPaths = new Set<string>();

/** Compare endpoints by path, so a query string cannot split one endpoint in two. */
function pathKeyOf(input: RequestInfo | URL | undefined | null): string {
  if (!input) return '';
  const raw =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : ((input as Request).url ?? '');
  if (!raw) return '';
  try {
    const base =
      typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    return new URL(raw, base).pathname;
  } catch {
    return raw;
  }
}

function noteRefused(key: string): void {
  if (key) refusedPaths.add(key);
}

/**
 * Revoke the challenge, but only on evidence that actually supports it.
 *
 * An unknown key (a hand-built Response has `url === ""`) is deliberately
 * treated as no evidence: failing closed here costs a redundant redirect,
 * failing open costs the user their only way out of the enrolment page.
 */
function resolveIfPreviouslyRefused(key: string): void {
  if (!key || !refusedPaths.has(key)) return;
  refusedPaths.clear();
  announceMfaResolved();
}

/**
 * Inspect a response, and if it is an MFA challenge announce it and throw.
 *
 * Call this before any other handling of a non-ok response - including before
 * a `403 -> null` branch - so the challenge cannot be mistaken for anything
 * else. Returns normally when there is no challenge.
 */
export async function throwIfMfaChallenge(res: Response): Promise<void> {
  const challenge = await detectMfaChallenge(res);
  if (!challenge) {
    // Only an endpoint that was previously refused can vouch for the
    // challenge being over. See `refusedPaths`.
    if (res.ok) resolveIfPreviouslyRefused(pathKeyOf(res.url));
    return;
  }
  noteRefused(pathKeyOf(res.url));
  announceMfaChallenge(challenge);
  throw new MfaEnrollmentRequiredError(challenge);
}

/**
 * `fetch` with the MFA challenge check already applied.
 *
 * Fetchers that do not need custom non-ok handling should use this instead of
 * calling `fetch` directly, so a challenge can never be silently dropped.
 */
export async function fetchWithMfaCheck(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetch(input, init);
  await throwIfMfaChallenge(res);
  return res;
}

/**
 * Install a one-time global `fetch` wrapper that notices MFA challenges.
 *
 * WHY A GLOBAL WRAPPER AND NOT 60 EDITS
 * -------------------------------------
 * The challenge can arrive on ANY request, because the middleware refuses
 * everything outside its exempt list. Wiring it per-fetcher means ~60 call
 * sites across components, contexts and hooks - and, worse, every call site
 * added afterwards has to remember. One missed fetcher turns the challenge
 * into a generic error toast and the user is never told to enrol, which is
 * exactly the failure this feature exists to prevent.
 *
 * WHY IT ONLY ANNOUNCES, AND NEVER THROWS
 * ---------------------------------------
 * This is the property that makes a global wrapper safe. It does not alter
 * the response, does not throw, and does not change any existing error
 * handling: a caller that got a 403 before still gets exactly that 403.
 * The only effect is that the route guard learns a challenge happened and can
 * redirect.
 *
 * Fetchers that need to stop rendering on a challenge - `dashboardApi`, whose
 * `getAdminOnly` would otherwise swallow it into `null` - still call
 * `throwIfMfaChallenge` explicitly. The two layers compose: the wrapper
 * guarantees the redirect, the explicit call guarantees the panel does not
 * render as though nothing happened.
 */
let interceptorInstalled = false;

export function installMfaChallengeInterceptor(): void {
  if (typeof window === 'undefined' || interceptorInstalled) return;
  const original = window.fetch;
  if (typeof original !== 'function') return;
  interceptorInstalled = true;

  // An arrow function on purpose: a `function` here would need an explicit
  // `this` annotation under noImplicitThis, and the wrapper has no use for a
  // dynamic receiver - fetch is always called on window.
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const res = await original.apply(window, args);
    try {
      // Cheap: only a 403 is ever inspected, and the body is read from a
      // clone so the caller's stream is untouched.
      if (res.status === 403) {
        const challenge = await detectMfaChallenge(res);
        if (challenge) {
          noteRefused(pathKeyOf(args[0]));
          announceMfaChallenge(challenge);
        }
      } else if (res.ok) {
        resolveIfPreviouslyRefused(pathKeyOf(args[0]));
      }
    } catch {
      // An observer must never break the request it is observing.
    }
    return res;
  };
}

/** Undo the interceptor. For tests. */
export function uninstallMfaChallengeInterceptor(original?: typeof fetch): void {
  // Tests reuse the module between cases; a refusal remembered from a
  // previous case would let an unrelated 200 revoke the next one's challenge.
  refusedPaths.clear();
  if (typeof window === 'undefined') return;
  interceptorInstalled = false;
  if (original) window.fetch = original;
}
